/**
 * @copyright (C) 2023, Rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of Rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by Rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @description
 *
 * @created at Sun Mar 05 2023
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */

import { EventEmitter } from "eventemitter3";
import { has } from "lodash-es";

import PriorityQueue from "./priorityqueue";

export type EventName = string;
export type Handler = (...args: any[]) => void;
export type EventHandlers = { [name: EventName]: Handler };
type EventModules = { [name: EventName]: EventHandler };

export default class EventHandler extends EventEmitter {
	private static moduleList: EventModules = {};
	private static eventQueue = new PriorityQueue<{ target: string; message: any[] }>();

	public static find(name: EventName) {
		return has(EventHandler.moduleList, name) ? EventHandler.moduleList[name] : null;
	}

	public static registry(moduleName: string, module: EventHandler) {
		if (EventHandler.find(moduleName) === null) {
			EventHandler.moduleList[moduleName] = module;
		}
	}

	public static unregistry(moduleName: string) {
		if (EventHandler.find(moduleName)) {
			delete EventHandler.moduleList[moduleName];
		}
	}

	public static install(events: EventHandlers) {
		for (const [name, handler] of Object.entries(events)) {
			const module = (() => {
				const module = EventHandler.find(name);
				return module ? module : new EventHandler(name);
			})();
			module.setEvents(name, handler);
		}
	}

	public static uninstall(events: EventHandlers) {
		for (const [name, handler] of Object.entries(events)) {
			const module = EventHandler.find(name);
			if (module) {
				module.unsetEvents(name, handler);
				EventHandler.unregistry(name);
			}
		}
	}

	public static emit(name: EventName, ...value: any[]) {
		EventHandler.find(name)?.send(name, ...value);
	}

	private static _priority_level: number = 1000;
	public static get EventQueue() {
		return this.eventQueue;
	}
	public static post(name: string, ...value: any[]) {
		this.eventQueue.enqueue(EventHandler._priority_level, { target: name, message: value });
		EventHandler._priority_level++;
		if (EventHandler._priority_level >= Number.MAX_SAFE_INTEGER) {
			EventHandler._priority_level = 0;
		}
	}
	public static send(name: string, priority: number, ...value: any[]) {
		if (priority >= 0 && priority < 1000) {
			this.eventQueue.enqueue(priority, { target: name, message: value });
		} else {
			console.warn("#EventHandler# Supports priority limits of less than 1000, your priortiy", priority);
		}
	}

	public static _send(name: EventName) {
		return (...value: any[]) => {
			EventHandler.find(name)?.send(`$${name}$`, ...value);
		};
	}

	public static fire(name: EventName, ...value: any[]) {
		if (name.startsWith("$") && name.endsWith("$") && name.includes("::")) {
			const _name = "on" + name.replace("::", "").replaceAll("$", "");
			return this.emit(_name, ...value);
		}

		return this.emit(name, ...value);
	}

	constructor(public moduleName: string) {
		super();

		EventHandler.registry(moduleName, this);
	}

	public dispose() {
		EventHandler.unregistry(this.moduleName);
	}

	public setEvents(name: EventName, handler: Handler) {
		this.on(name, handler);
	}

	public unsetEvents(name: EventName, handler: Handler) {
		this.off(name, handler);
	}

	public send(name: EventName, ...value: any[]) {
		this.emit(name, ...value);
	}
}
